import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { CheckCircle2, ArrowRight } from "lucide-react";
import { SEO } from "@/components/SEO";
import { ServiceAreasSection } from "@/components/CrossLinks";

export default function Asphalt() {
  const schema = [
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: "https://www.saskroofingcompany.com/",
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Services",
          item: "https://www.saskroofingcompany.com/services",
        },
        {
          "@type": "ListItem",
          position: 3,
          name: "Asphalt Shingle Roofing",
          item: "https://www.saskroofingcompany.com/services/asphalt",
        },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "Service",
      "@id": "https://www.saskroofingcompany.com/services/asphalt#service",
      serviceType: "Asphalt Shingle Roofing",
      name: "Asphalt Shingle Roofing",
      description:
        "High-quality asphalt shingle roofing designed for Saskatchewan's extreme climate.",
      url: "https://www.saskroofingcompany.com/services/asphalt",
      provider: {
        "@id": "https://www.saskroofingcompany.com/#localbusiness",
        "@type": "RoofingContractor",
        name: "Sask Roofing Company",
        url: "https://www.saskroofingcompany.com/",
        telephone: "+1-639-739-7969",
      },
      areaServed: [
        {
          "@type": "City",
          name: "Saskatoon",
          containedInPlace: { "@type": "State", name: "Saskatchewan" },
        },
        {
          "@type": "City",
          name: "Regina",
          containedInPlace: { "@type": "State", name: "Saskatchewan" },
        },
        {
          "@type": "City",
          name: "Moose Jaw",
          containedInPlace: { "@type": "State", name: "Saskatchewan" },
        },
        {
          "@type": "City",
          name: "Prince Albert",
          containedInPlace: { "@type": "State", name: "Saskatchewan" },
        },
        {
          "@type": "City",
          name: "Swift Current",
          containedInPlace: { "@type": "State", name: "Saskatchewan" },
        },
        {
          "@type": "City",
          name: "Yorkton",
          containedInPlace: { "@type": "State", name: "Saskatchewan" },
        },
        {
          "@type": "City",
          name: "Estevan",
          containedInPlace: { "@type": "State", name: "Saskatchewan" },
        },
        {
          "@type": "City",
          name: "Weyburn",
          containedInPlace: { "@type": "State", name: "Saskatchewan" },
        },
      ],
      offers: {
        "@type": "AggregateOffer",
        priceCurrency: "CAD",
        lowPrice: "6000",
        highPrice: "15000",
      },
      availableChannel: {
        "@type": "ServiceChannel",
        serviceUrl: "https://www.saskroofingcompany.com/contact",
        servicePhone: {
          "@type": "ContactPoint",
          telephone: "+1-639-739-7969",
          contactType: "customer service",
          areaServed: "CA-SK",
          availableLanguage: "English",
        },
      },
      termsOfService: "https://www.saskroofingcompany.com/about#warranty",
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: "4.9",
        reviewCount: "87",
        bestRating: "5",
        worstRating: "1",
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: [
        {
          "@type": "Question",
          name: "How long do asphalt shingles last in Saskatchewan?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "In Saskatchewan, high-quality architectural asphalt shingles typically last 20 to 30 years depending on weather exposure and proper ventilation.",
          },
        },
        {
          "@type": "Question",
          name: "Can you install asphalt shingles in the winter?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Yes, we can perform winter installations, though special techniques are required to ensure the shingles seal correctly in cold temperatures.",
          },
        },
        {
          "@type": "Question",
          name: "Are architectural shingles better than 3-tab?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Absolutely. Architectural shingles are thicker, offer better wind resistance, last longer, and provide a much more appealing dimensional look compared to flat 3-tab shingles. We rarely recommend 3-tab for residential roofing in Saskatchewan.",
          },
        },
      ],
    },
  ];

  return (
    <div className="flex flex-col min-h-screen">
      <SEO
        title="Asphalt Shingle Roofing Saskatchewan | Sask Roofing"
        description="Expert asphalt shingle roofing in Saskatchewan. We install architectural shingles built to withstand high winds, hail, and extreme winter temperatures."
        schema={schema}
      />

      {/* Hero */}
      <section className="relative py-32 text-primary-foreground overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/04b10f0a-3e4a-4a6e-a03b-9f26ff276b90.png&output=webp&q=60"
            alt="Asphalt Shingle Roofing"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/40"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
        </div>
        <div className="container relative px-4 z-10">
          <div className="max-w-3xl space-y-6">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-white drop-shadow-md">
              Asphalt Shingle Roofing Saskatchewan
            </h1>
            <p className="text-xl text-white/90 drop-shadow-sm font-medium">
              Asphalt shingle roofing provides durable, cost-effective
              protection for Saskatchewan homes. We install premium
              architectural shingles engineered to survive our extreme
              temperature swings and high prairie winds.
            </p>
            <div className="pt-4">
              <Button
                size="lg"
                className="bg-accent text-accent-foreground hover:bg-accent/90 shadow-lg"
                asChild
              >
                <Link to="/contact">Get a Free Asphalt Quote</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* When you need it & How it works */}
      <section className="py-20 bg-background">
        <div className="container px-4">
          <div className="grid md:grid-cols-2 gap-12">
            <div className="space-y-6">
              <h2 className="text-3xl font-bold">
                When You Need Asphalt Shingle Replacement
              </h2>
              <p className="text-lg text-muted-foreground">
                You need a new asphalt roof when your current shingles are
                curling, cracking, missing granules, or if your roof is over 20
                years old and showing signs of active leaking. Waiting too long
                leads to costly plywood rot and interior water damage.
              </p>
              <h3 className="text-2xl font-bold pt-4">How It Works</h3>
              <p className="text-lg text-muted-foreground">
                We remove your old roofing down to the deck to inspect for rot.
                Then, we install high-performance underlayment, ice and water
                shield at the eaves, and nail down premium architectural
                shingles using enhanced wind-resistant nailing patterns.
              </p>
            </div>
            <div className="bg-muted/30 p-8 rounded-xl border">
              <h3 className="text-2xl font-bold mb-6">
                Why It Suits Saskatchewan
              </h3>
              <ul className="space-y-4">
                {[
                  "Flexibility to handle -40°C to +35°C temperature swings without cracking.",
                  "Class 3 or 4 impact resistance available for hail-prone areas.",
                  "High wind ratings (up to 210 km/h) to prevent blow-offs during prairie storms.",
                  "Cost-effective balance of longevity and upfront investment.",
                ].map((item, i) => (
                  <li key={i} className="flex gap-3 text-muted-foreground">
                    <CheckCircle2 className="w-6 h-6 text-primary shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Materials */}
      <section className="py-20 bg-muted/30">
        <div className="container px-4 max-w-4xl">
          <div className="space-y-6">
            <h2 className="text-3xl font-bold">
              Materials We Use for Asphalt Roofing
            </h2>
            <p className="text-lg text-muted-foreground">
              We exclusively install premium architectural shingles from
              industry-leading manufacturers like IKO, CertainTeed, GAF, and BP.
              Our standard installations feature Class 3 or Class 4
              impact-resistant options, designed specifically to withstand
              severe Saskatchewan hailstorms. We offer a wide range of colors to
              perfectly match your home's exterior, ensuring both maximum
              protection and elevated curb appeal. Every shingle is backed by
              robust manufacturer warranties and our own workmanship guarantee.
            </p>
            <div className="mt-8">
              <img
                src="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/2de89809-eea4-46a6-aaf9-fe65daf5aa25.png&output=webp&q=60"
                alt="Premium architectural asphalt shingles close-up"
                className="rounded-xl shadow-md w-full h-80 object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Material Comparison Table */}
      <section className="py-20 bg-background">
        <div className="container px-4 max-w-5xl">
          <h2 className="text-3xl font-bold text-center mb-12">
            Asphalt vs. Metal: Which is Right for You?
          </h2>
          <div className="overflow-x-auto rounded-xl border shadow-sm">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-muted">
                  <th className="p-4 font-bold border-b">Feature</th>
                  <th className="p-4 font-bold border-b text-primary">
                    Architectural Asphalt
                  </th>
                  <th className="p-4 font-bold border-b">
                    Standing Seam Metal
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y">
                <tr>
                  <td className="p-4 font-medium">Upfront Cost</td>
                  <td className="p-4 text-muted-foreground">
                    $$ (Most affordable)
                  </td>
                  <td className="p-4 text-muted-foreground">
                    $$$$ (Premium investment)
                  </td>
                </tr>
                <tr className="bg-muted/30">
                  <td className="p-4 font-medium">Expected Lifespan</td>
                  <td className="p-4 text-muted-foreground">20 - 30 Years</td>
                  <td className="p-4 text-muted-foreground">50+ Years</td>
                </tr>
                <tr>
                  <td className="p-4 font-medium">Hail Resistance</td>
                  <td className="p-4 text-muted-foreground">
                    Good (Class 3/4 available)
                  </td>
                  <td className="p-4 text-muted-foreground">
                    Excellent (Won't crack/puncture)
                  </td>
                </tr>
                <tr className="bg-muted/30">
                  <td className="p-4 font-medium">Maintenance</td>
                  <td className="p-4 text-muted-foreground">
                    Low (Occasional inspections)
                  </td>
                  <td className="p-4 text-muted-foreground">
                    Very Low (Virtually maintenance-free)
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Cost & Process */}
      <section className="py-20 bg-muted/50">
        <div className="container px-4">
          <div className="grid md:grid-cols-2 gap-12 mb-12">
            <div className="space-y-6">
              <h2 className="text-3xl font-bold">What It Costs</h2>
              <div className="bg-white p-6 rounded-xl border border-primary/20 shadow-sm mb-6">
                <div className="text-sm font-bold text-primary uppercase tracking-wider mb-2">
                  Estimated Investment
                </div>
                <div className="text-3xl font-bold text-foreground">
                  $5,000 - $12,000+
                </div>
                <p className="text-sm text-muted-foreground mt-2">
                  *Rough estimate for an average-sized Saskatchewan home. Final
                  cost depends on pitch and materials.
                </p>
              </div>
              <p className="text-lg text-muted-foreground">
                An asphalt shingle roof replacement in Saskatchewan typically
                costs between $5,000 and $12,000 for an average-sized home,
                depending on the roof's complexity, pitch, and the specific
                grade of architectural shingle selected. For a deep dive into
                pricing factors, read our{" "}
                <Link
                  to="/roofing-cost-guide-saskatchewan"
                  className="text-accent hover:underline"
                >
                  Saskatchewan Roofing Cost Guide
                </Link>
                . We always provide honest, fixed-price quotes in writing—no
                surprise fees.
              </p>
              <div className="mt-8 rounded-2xl overflow-hidden shadow-2xl h-64">
                <img
                  src="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/a064f76a-cb98-4f5b-8cb3-4a80f8281705.png&output=webp&q=60"
                  alt="Completed Asphalt Shingle Roof"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            <div className="space-y-6">
              <h2 className="text-3xl font-bold">Our Process</h2>
              <div className="space-y-6 mt-8">
                {[
                  {
                    title: "Free Inspection",
                    desc: "We assess your roof, ventilation, and decking.",
                  },
                  {
                    title: "Material Selection",
                    desc: "Choose colors and impact-rating options.",
                  },
                  {
                    title: "Tear-off & Prep",
                    desc: "Complete removal of old materials.",
                  },
                  {
                    title: "Expert Installation",
                    desc: "Built to manufacturer and local code specs.",
                  },
                  {
                    title: "Final Walkthrough",
                    desc: "Magnetic sweeps leave your yard spotless and we review the work.",
                  },
                ].map((step, idx) => (
                  <div key={idx} className="flex gap-4">
                    <div className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold shrink-0">
                      {idx + 1}
                    </div>
                    <div>
                      <h3 className="text-xl font-bold">{step.title}</h3>
                      <p className="text-muted-foreground mt-1">{step.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <ServiceAreasSection
        service="Asphalt Shingle Roofing"
        serviceSlug="asphalt"
      />

      {/* FAQ */}
      <section className="py-20 bg-background">
        <div className="container px-4 max-w-4xl">
          <h2 className="text-3xl font-bold text-center mb-12">
            Asphalt Roofing FAQ
          </h2>
          <Accordion type="single" collapsible className="w-full">
            <AccordionItem value="item-1">
              <AccordionTrigger className="text-lg font-medium text-left">
                How long do asphalt shingles last in Saskatchewan?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground text-base">
                In Saskatchewan, high-quality architectural asphalt shingles
                typically last 20 to 30 years depending on weather exposure and
                proper ventilation.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-2">
              <AccordionTrigger className="text-lg font-medium text-left">
                Can you install asphalt shingles in the winter?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground text-base">
                Yes, we can perform winter installations, though special
                techniques are required to ensure the shingles seal correctly in
                cold temperatures.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-3">
              <AccordionTrigger className="text-lg font-medium text-left">
                Are architectural shingles better than 3-tab?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground text-base">
                Absolutely. Architectural shingles are thicker, offer better
                wind resistance, last longer, and provide a much more appealing
                dimensional look compared to flat 3-tab shingles. We rarely
                recommend 3-tab for residential roofing in Saskatchewan.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* Cross Links & CTA */}
      <section className="py-20 bg-primary text-primary-foreground text-center">
        <div className="container px-4 max-w-3xl space-y-8">
          <h2 className="text-3xl font-bold">Ready for a Reliable Roof?</h2>
          <p className="text-xl text-primary-foreground/80">
            We proudly serve Saskatoon, Regina, Moose Jaw, Prince Albert, Swift
            Current, Yorkton, Estevan, and Weyburn. Most asphalt shingle
            projects take just 1-2 days. Book your free inspection today.
            Looking for an upgrade? Consider our{" "}
            <Link to="/services/metal" className="underline hover:text-accent">
              metal roofing
            </Link>{" "}
            options.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
            <Button
              size="lg"
              className="bg-accent text-accent-foreground hover:bg-accent/90"
              asChild
            >
              <Link to="/contact">Schedule a Free Asphalt Inspection</Link>
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="bg-transparent border-white text-white hover:bg-white/10"
              asChild
            >
              <Link to="/process">See Our Process</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
